import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: on 401, try a single refresh and retry the request.
let refreshPromise = null;

const performLogout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Don't try to refresh the refresh call itself, or already-retried requests
    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login')
    ) {
      if (status === 401 && localStorage.getItem('access_token')) {
        // Unauthenticated and no path forward — clear and bounce.
        performLogout();
      }
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      if (localStorage.getItem('access_token')) performLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken })
          .then((res) => {
            localStorage.setItem('access_token', res.data.access_token);
            if (res.data.refresh_token) {
              localStorage.setItem('refresh_token', res.data.refresh_token);
            }
            return res.data.access_token;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const newToken = await refreshPromise;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      performLogout();
      return Promise.reject(refreshError);
    }
  }
);

export default api;

// Helper function to extract error message from API response
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (!error?.response?.data) {
    return error?.message || defaultMessage;
  }

  const detail = error.response.data.detail;

  // If detail is a string, return it
  if (typeof detail === 'string') {
    return detail;
  }

  // If detail is an array (validation errors), extract messages
  if (Array.isArray(detail)) {
    if (detail.length === 0) {
      return defaultMessage;
    }
    // Extract the first error message
    const firstError = detail[0];
    if (firstError?.msg) {
      return firstError.msg;
    }
    // Fallback to string representation
    return String(firstError);
  }

  // If detail is an object, try to extract message
  if (typeof detail === 'object' && detail !== null) {
    if (detail.msg) {
      return detail.msg;
    }
    if (detail.message) {
      return detail.message;
    }
  }

  return defaultMessage;
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestPasswordReset: (data) => api.post('/auth/password-reset/request', data),
  resetPasswordConfirm: (data) => api.post('/auth/password-reset/confirm', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/auth/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  enable2FA: () => api.post('/auth/2fa/enable'),
  verify2FA: (code) => api.post('/auth/2fa/verify', { totp_code: code }),
  disable2FA: (code) => api.post('/auth/2fa/disable', { totp_code: code }),
  // Email verification
  sendEmailLink: (data) => api.post('/auth/email/send-link', data),
  verifyEmailLink: (token) => api.post(`/auth/email/verify-link?token=${token}`),
  resendEmailLink: (data) => api.post('/auth/email/resend-link', data),
  // Phone verification
  sendPhoneCode: (data) => api.post('/auth/phone/send-code', data),
  verifyPhoneCode: (data) => api.post('/auth/phone/verify', data),
  // Verification status
  getVerificationStatus: () => api.get('/verification-status'),
};

// KYC API
export const kycAPI = {
  submit: (formData) => api.post('/kyc/submit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStatus: () => api.get('/kyc/status'),
};

// Wallet API
export const walletAPI = {
  getAll: () => api.get('/wallets'),
  getBySymbol: (symbol) => api.get(`/wallets/${symbol}`),
};

// Asset API
export const assetAPI = {
  getAll: () => api.get('/assets'),
};

// Trade API
export const tradeAPI = {
  request: (data) => api.post('/trades/request', data),
  getAll: () => api.get('/trades'),
  getById: (id) => api.get(`/trades/${id}`),
  acceptQuote: (tradeId) => api.post('/trades/accept-quote', { trade_id: tradeId }),
  declineQuote: (tradeId) => api.post('/trades/decline-quote', { trade_id: tradeId }),
};

// Deposit API
export const depositAPI = {
  request: (data) => api.post('/deposits/request', data),
  uploadReceipt: (depositId, file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    return api.post(`/deposits/${depositId}/upload-receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: () => api.get('/deposits'),
};

// Withdrawal API
export const withdrawalAPI = {
  request: (data) => api.post('/withdrawals/request-with-verification', data),
  verify: (withdrawalId, code) => {
    const formData = new FormData();
    formData.append('withdrawal_id', withdrawalId);
    formData.append('verification_code', code);
    return api.post('/withdrawals/verify-and-complete', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  resendCode: (withdrawalId) => {
    const formData = new FormData();
    formData.append('withdrawal_id', withdrawalId);
    return api.post('/withdrawals/resend-code', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  cancel: (withdrawalId) => api.delete(`/withdrawals/cancel/${withdrawalId}`),
  getAll: () => api.get('/withdrawals'),
};

// Transfer API
export const transferAPI = {
  request: (data) => api.post('/transfers/request', data),
  verify: (transferId, code) => api.post('/transfers/verify', { transfer_id: transferId, verification_code: code }),
  getAll: () => api.get('/transfers'),
};

// Transaction API
export const transactionAPI = {
  getAll: () => api.get('/transactions'),
};

// Ticket API
export const ticketAPI = {
  create: (data) => api.post('/tickets', data),
  getAll: () => api.get('/tickets'),
  getById: (id) => api.get(`/tickets/${id}`),
  addMessage: (ticketId, message) => api.post(`/tickets/${ticketId}/message`, { ticket_id: ticketId, message }),
};

// Direct Message API
export const dmAPI = {
  searchUsers: (query) => api.get('/users/search', { params: { q: query } }),
  getConversations: () => api.get('/dm/conversations'),
  createConversation: (participantId) =>
    api.post('/dm/conversations', { participant_id: participantId }),
  getMessages: (conversationId) =>
    api.get(`/dm/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, message) =>
    api.post(`/dm/conversations/${conversationId}/messages`, { message }),
};

// Community API
export const communityAPI = {
  getPosts: () => api.get('/community/posts'),
  createPost: (data) => api.post('/community/posts', data),
  toggleLike: (postId) => api.post(`/community/posts/${postId}/like`),
  addComment: (postId, data) =>
    api.post(`/community/posts/${postId}/comment`, data),
  getTrendingTopics: () => api.get('/community/trending-topics'),
  getTopTraders: () => api.get('/community/top-traders'),
};

// Notification API
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: async () => {
    // No bulk endpoint; loop through unread ids.
    const { data } = await api.get('/notifications');
    const unread = (Array.isArray(data) ? data : []).filter((n) => !n.is_read);
    await Promise.all(unread.map((n) => api.post(`/notifications/${n.id}/read`)));
    return unread.length;
  },
};

// Card API
export const cardAPI = {
  getAll: () => api.get('/cards'),
  add: (data) => api.post('/cards', data),
  delete: (cardId) => api.delete(`/cards/${cardId}`),
  setDefault: (cardId) => api.post(`/cards/${cardId}/default`),
};

// Bank Account API
export const bankAccountAPI = {
  getAll: () => api.get('/bank-accounts'),
  add: (data) => api.post('/bank-accounts', data),
  delete: (accountId) => api.delete(`/bank-accounts/${accountId}`)
};

// Trading API
export const tradingAPI = {
  placeOrder: (data) => api.post('/trading/order', data),
  getOrders: () => api.get('/trading/orders'),
  cancelOrder: (orderId) => api.delete(`/trading/order/${orderId}`),
  getPositions: () => api.get('/trading/positions'),
  closePosition: (positionId) => api.post(`/trading/position/${positionId}/close`)
};

// Swap API
export const swapAPI = {
  request: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/swap/request', formData);
  },
  getHistory: () => api.get('/swap/history'),
};

// Limit Orders API
export const limitOrderAPI = {
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/orders/limit', formData);
  },
  getAll: () => api.get('/orders/limit'),
  cancel: (orderId) => api.delete(`/orders/limit/${orderId}`),
};

// Scheduled Orders API
export const scheduledOrderAPI = {
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/orders/scheduled', formData);
  },
  getAll: () => api.get('/orders/scheduled'),
  cancel: (orderId) => api.delete(`/orders/scheduled/${orderId}`),
};

// Crypto Address API
export const cryptoAddressAPI = {
  add: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/crypto-addresses', formData);
  },
  getAll: () => api.get('/crypto-addresses'),
  delete: (addressId) => api.delete(`/crypto-addresses/${addressId}`),
};

// Preferences API
export const preferencesAPI = {
  get: () => api.get('/preferences'),
  update: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.put('/preferences', formData);
  },
};

// Markets API
export const marketsAPI = {
  getCryptocurrencies: () => api.get('/markets/cryptocurrencies'),
  getGlobal: () => api.get('/markets/global'),
};

// Mining API — Bitcoin Mining contracts. Purchase debits the chosen wallet
// and activates a rolling 24h mining cycle; the backend simulator emits
// per-minute reward ticks that feed the live dashboard chart.
export const miningAPI = {
  purchase: (data) => api.post('/mining/purchase', data),
  getContract: () => api.get('/mining/contract'),
  getStats: () => api.get('/mining/stats'),
  getRewardsChart: (hours = 24) => api.get(`/mining/rewards/chart?hours=${hours}`),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  suspendUser: (userId) => api.post(`/admin/users/${userId}/suspend`),
  activateUser: (userId) => api.post(`/admin/users/${userId}/activate`),

  // KYC
  getKYCList: (params) => api.get('/admin/kyc', { params }),
  getKYC: (kycId) => api.get(`/admin/kyc/${kycId}`),
  kycAction: (data) => api.post('/admin/kyc/action', data),

  // Trades
  getTrades: (params) => api.get('/admin/trades', { params }),
  sendQuote: (data) => api.post('/admin/trades/send-quote', data),
  tradeAction: (data) => api.post('/admin/trades/action', data),

  // Deposits
  getDeposits: (params) => api.get('/admin/deposits', { params }),
  depositAction: (data) => api.post('/admin/deposits/action', data),

  // Withdrawals
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  withdrawalAction: (data) => api.post('/admin/withdrawals/action', data),

  // Tickets
  getTickets: (params) => api.get('/admin/tickets', { params }),
  replyTicket: (ticketId, message) => api.post(`/admin/tickets/${ticketId}/reply`, { ticket_id: ticketId, message }),
  closeTicket: (ticketId) => api.post(`/admin/tickets/${ticketId}/close`),

  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),

  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),

  // Mining
  getMiningContracts: (params) => api.get('/admin/mining/contracts', { params }),
  miningContractAction: (contractId, action) =>
    api.post(`/admin/mining/contracts/${contractId}/action`, { action }),
};
